import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Bell, Plus, Trash2, ToggleLeft, ToggleRight, Loader2, MapPin, Search, Briefcase } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;


const EMPTY_FORM = {
  name: '',
  keywords: '',
  location: '',
  job_type: 'any',
  salary_min: '',
  salary_max: '',
  frequency: 'daily',
};

export default function JobAlertsPage() {
  const { token, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login?redirect=/alerts');
      return;
    }
    fetchAlerts();
  }, [isAuthenticated]);

  const authHeaders = () => ({ headers: { Authorization: `Bearer ${token}` } });

  const fetchAlerts = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/alerts`, authHeaders());
      setAlerts(res.data || []);
    } catch {
      setAlerts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.name.trim()) {
      toast.error('Please enter an alert name');
      return;
    }
    setCreating(true);
    try {
      const payload = {
        ...formData,
        salary_min: formData.salary_min ? Number(formData.salary_min) : null,
        salary_max: formData.salary_max ? Number(formData.salary_max) : null,
      };
      await axios.post(`${API_URL}/api/alerts`, payload, authHeaders());
      toast.success('Job alert created!');
      setShowCreate(false);
      setFormData(EMPTY_FORM);
      fetchAlerts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create alert');
    } finally {
      setCreating(false);
    }
  };

  const handleToggle = async (alert) => {
    try {
      await axios.put(
        `${API_URL}/api/alerts/${alert.id}`,
        { is_active: !alert.is_active },
        authHeaders()
      );
      setAlerts((prev) =>
        prev.map((a) => (a.id === alert.id ? { ...a, is_active: !a.is_active } : a))
      );
      toast.success(alert.is_active ? 'Alert paused' : 'Alert activated');
    } catch (e) {
      toast.error('Failed to update alert');
    }
  };

  const handleDelete = async (alertId) => {
    try {
      await axios.delete(`${API_URL}/api/alerts/${alertId}`, authHeaders());
      setAlerts((prev) => prev.filter((a) => a.id !== alertId));
      toast.success('Alert deleted');
    } catch (e) {
      toast.error('Failed to delete alert');
    }
  };

  const frequencyLabel = (f) => {
    if (f === 'daily') return 'Daily';
    if (f === 'weekly') return 'Weekly';
    if (f === 'instant') return 'Instant';
    return f;
  };

  const jobTypeLabel = (t) => {
    const map = { any: 'Any type', full_time: 'Full-time', part_time: 'Part-time', contract: 'Contract', freelance: 'Freelance' };
    return map[t] || t;
  };

  return (
    <div className="min-h-screen bg-[#F0F4F8]">
      {/* Hero */}
      <div className="bg-gradient-to-r from-[#003366] to-[#004080] text-white py-14">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Bell className="w-6 h-6 text-white/80" />
                <span className="text-white/70 text-sm font-medium uppercase tracking-wide">Notifications</span>
              </div>
              <h1 className="text-3xl font-bold mb-2">Job Alerts</h1>
              <p className="text-white/75 text-base">
                Stay informed — get notified when new jobs matching your criteria are posted.
              </p>
            </div>
            <Button
              onClick={() => setShowCreate(true)}
              className="bg-[#E11D48] hover:bg-[#E11D48]/90 shrink-0"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Alert
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-[#003366]" />
          </div>
        ) : alerts.length === 0 ? (
          <Card>
            <CardContent className="p-14 text-center">
              <Bell className="w-14 h-14 text-muted-foreground/30 mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">No job alerts yet</h3>
              <p className="text-muted-foreground mb-6">
                Create your first alert and never miss a relevant job opportunity again.
              </p>
              <Button onClick={() => setShowCreate(true)} className="bg-[#003366] hover:bg-[#003366]/90">
                <Plus className="w-4 h-4 mr-2" />
                Create Your First Alert
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">{alerts.length} alert{alerts.length !== 1 ? 's' : ''} configured</p>
            {alerts.map((alert) => (
              <Card key={alert.id} className={`transition-all ${alert.is_active ? '' : 'opacity-70'}`}>
                <CardContent className="p-5">
                  <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2 flex-wrap">
                        <h3 className="font-semibold text-lg">{alert.name}</h3>
                        <Badge
                          className={`text-xs border-0 ${
                            alert.frequency === 'instant'
                              ? 'bg-red-100 text-red-700'
                              : alert.frequency === 'daily'
                              ? 'bg-blue-100 text-blue-700'
                              : 'bg-purple-100 text-purple-700'
                          }`}
                        >
                          {frequencyLabel(alert.frequency)}
                        </Badge>
                        <Badge
                          className={`text-xs border-0 ${alert.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}
                        >
                          {alert.is_active ? 'Active' : 'Paused'}
                        </Badge>
                      </div>

                      <div className="flex flex-wrap gap-x-5 gap-y-1.5 text-sm text-muted-foreground">
                        {alert.keywords && (
                          <div className="flex items-center gap-1.5">
                            <Search className="w-3.5 h-3.5" />
                            <span>{alert.keywords}</span>
                          </div>
                        )}
                        {alert.location && (
                          <div className="flex items-center gap-1.5">
                            <MapPin className="w-3.5 h-3.5" />
                            <span>{alert.location}</span>
                          </div>
                        )}
                        {alert.job_type && alert.job_type !== 'any' && (
                          <div className="flex items-center gap-1.5">
                            <Briefcase className="w-3.5 h-3.5" />
                            <span>{jobTypeLabel(alert.job_type)}</span>
                          </div>
                        )}
                        {(alert.salary_min || alert.salary_max) && (
                          <div className="flex items-center gap-1.5">
                            <span>
                              £{alert.salary_min ? alert.salary_min.toLocaleString() : '0'}
                              {alert.salary_max ? ` – £${alert.salary_max.toLocaleString()}` : '+'}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        onClick={() => handleToggle(alert)}
                        className="flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-lg hover:bg-secondary transition-colors"
                        title={alert.is_active ? 'Pause alert' : 'Activate alert'}
                      >
                        {alert.is_active ? (
                          <ToggleRight className="w-5 h-5 text-emerald-500" />
                        ) : (
                          <ToggleLeft className="w-5 h-5 text-gray-400" />
                        )}
                        <span className="hidden sm:block">{alert.is_active ? 'Pause' : 'Activate'}</span>
                      </button>
                      <button
                        onClick={() => handleDelete(alert.id)}
                        className="flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-lg hover:bg-red-50 text-red-600 transition-colors"
                        title="Delete alert"
                      >
                        <Trash2 className="w-4 h-4" />
                        <span className="hidden sm:block">Delete</span>
                      </button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Create Alert Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Job Alert</DialogTitle>
            <DialogDescription>
              Set your preferences and we'll notify you when matching jobs are posted.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Alert Name *</Label>
              <Input
                placeholder="e.g. Senior Developer Roles in London"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Keywords</Label>
              <Input
                placeholder="e.g. React, Python, Marketing"
                value={formData.keywords}
                onChange={(e) => setFormData({ ...formData, keywords: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Location</Label>
              <Input
                placeholder="e.g. London, Manchester, Remote"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Job Type</Label>
              <Select value={formData.job_type} onValueChange={(v) => setFormData({ ...formData, job_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any type</SelectItem>
                  <SelectItem value="full_time">Full-time</SelectItem>
                  <SelectItem value="part_time">Part-time</SelectItem>
                  <SelectItem value="contract">Contract</SelectItem>
                  <SelectItem value="freelance">Freelance</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Min Salary (£)</Label>
                <Input
                  type="number"
                  placeholder="e.g. 30000"
                  value={formData.salary_min}
                  onChange={(e) => setFormData({ ...formData, salary_min: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Max Salary (£)</Label>
                <Input
                  type="number"
                  placeholder="e.g. 60000"
                  value={formData.salary_max}
                  onChange={(e) => setFormData({ ...formData, salary_max: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Notification Frequency</Label>
              <Select value={formData.frequency} onValueChange={(v) => setFormData({ ...formData, frequency: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="instant">Instant</SelectItem>
                  <SelectItem value="daily">Daily digest</SelectItem>
                  <SelectItem value="weekly">Weekly digest</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={creating} className="bg-[#003366] hover:bg-[#003366]/90">
              {creating && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Create Alert
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
